// @ts-nocheck
import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import Button from 'flarum/common/components/Button';
import CommentPost from 'flarum/forum/components/CommentPost';

// PHP side uses boolval in serializeToForum, so values arrive as true/false/null.
const settingEnabled = function (key, defaultValue) {
  var _app$forum;
  if (defaultValue === void 0) {
    defaultValue = false;
  }
  const val = (_app$forum = app.forum) == null ? void 0 : _app$forum.attribute(key);
  if (val === null || val === undefined) return defaultValue;
  return !!val;
};
const isExternalLink = link => {
  try {
    const url = new URL(link.href, window.location.href);
    return url.hostname !== window.location.hostname;
  } catch (_) {
    return false;
  }
};
const openLogin = () => flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal').then(M => app.modal.show(M));
const openSignUp = () => flarum.reg.asyncModuleImport('flarum/forum/components/SignUpModal').then(M => app.modal.show(M));

// Build the lock placeholder span used in place of (or alongside) gated links.
const buildPlaceholder = label => {
  const placeholder = document.createElement('span');
  placeholder.className = 'GuestCtaLink';
  placeholder.setAttribute('data-guestcta-gated', '1');
  placeholder.setAttribute('role', 'button');
  placeholder.tabIndex = 0;
  const icon = document.createElement('i');
  icon.className = 'fas fa-lock';
  icon.setAttribute('aria-hidden', 'true');
  const text = document.createElement('span');
  text.className = 'GuestCtaLink-label';
  text.textContent = label;
  placeholder.appendChild(icon);
  placeholder.appendChild(text);
  placeholder.addEventListener('click', openLogin);
  placeholder.addEventListener('keydown', e => {
    if (e.key === 'Enter' || e.key === ' ') openLogin();
  });
  return placeholder;
};

// A link is "raw" when its visible text is just the URL itself (or empty).
// In that case there's no descriptive context to preserve, so we replace the
// whole link with the placeholder — otherwise we'd leave an ugly bare URL.
const isRawUrlLink = link => {
  const text = (link.textContent || '').trim();
  if (!text) return true;
  if (text === link.href) return true;
  if (/^https?:\/\//i.test(text)) return true;
  return false;
};

// Replace external links inside post bodies with a clickable lock placeholder.
// data-guestcta-gated prevents double-processing on onupdate redraws.
const gateGuestLinks = component => {
  if (app.session.user) return;
  if (!settingEnabled('guestCtaHideLinks', false)) return;
  const root = component.element;
  if (!root) return;
  const body = root.querySelector('.Post-body');
  if (!body) return;
  const label = app.translator.trans('ramon-guest-cta.forum.link_cta.placeholder', {}, true);
  body.querySelectorAll('a[href]:not([data-guestcta-gated])').forEach(link => {
    if (!isExternalLink(link)) return;
    const placeholder = buildPlaceholder(label);
    const parent = link.parentNode;
    if (!parent) return;
    if (isRawUrlLink(link)) {
      parent.replaceChild(placeholder, link);
      return;
    }

    // Preserve the original link's visible text/markup so the surrounding
    // sentence still makes sense, then append " (placeholder)" right after.
    while (link.firstChild) {
      parent.insertBefore(link.firstChild, link);
    }
    parent.insertBefore(document.createTextNode(' ('), link);
    parent.insertBefore(placeholder, link);
    parent.insertBefore(document.createTextNode(')'), link);
    parent.removeChild(link);
  });
};
app.initializers.add('ramon-guest-cta', () => {
  // ── Guest link gating ──────────────────────────────────────────────────────
  // CommentPost oncreate/onupdate fire whenever a post is rendered/redrawn.
  extend(CommentPost.prototype, 'oncreate', function () {
    gateGuestLinks(this);
  });
  extend(CommentPost.prototype, 'onupdate', function () {
    gateGuestLinks(this);
  });

  // ── CTA between posts (guest-only, configurable position 1-5) ──────────────
  // PostStream is code-split — use the string-based extend so flarum.reg.onLoad
  // applies the patch when the chunk evaluates. Same pattern flarum/realtime uses.
  const getCtaPosition = () => {
    try {
      var _app$forum$attribute, _app$forum2;
      const pos = (_app$forum$attribute = (_app$forum2 = app.forum) == null ? void 0 : _app$forum2.attribute('guestCtaPosition')) != null ? _app$forum$attribute : '1';
      const parsed = parseInt(pos, 10);
      return parsed >= 1 && parsed <= 5 ? parsed : 1;
    } catch (_) {
      return 1;
    }
  };
  const renderCta = () => m("div", {
    className: "PostStream-item PostStream-guestCta"
  }, m("div", {
    className: "GuestCta-wrapper"
  }, m("div", {
    className: "GuestCta"
  }, m("span", {
    className: "GuestCta-text"
  }, app.translator.trans('ramon-guest-cta.forum.post_cta.text')), m("span", {
    className: "GuestCta-buttons"
  }, m(Button, {
    className: "Button Button--primary GuestCta-btn GuestCta-btn--login",
    icon: "fas fa-sign-in-alt",
    onclick: openLogin
  }, app.translator.trans('core.forum.header.log_in_link')), m("span", {
    className: "GuestCta-or"
  }, app.translator.trans('ramon-guest-cta.forum.post_cta.or')), m(Button, {
    className: "Button GuestCta-btn GuestCta-btn--signup",
    icon: "fas fa-user-plus",
    onclick: openSignUp
  }, app.translator.trans('core.forum.header.sign_up_link'))))));
  const ctaEnabled = () => settingEnabled('guestCtaShowPostCta', false);

  // Position 1: afterFirstPostItems — Flarum-native hook, fully Mithril-managed.
  extend('flarum/forum/components/PostStream', 'afterFirstPostItems', function (items) {
    if (!ctaEnabled() || app.session.user) return;
    if (getCtaPosition() === 1) items.add('guest-cta', renderCta(), 100);
  });

  // Positions 2-5: extend view() and splice the CTA vnode after the N-th comment post.
  // Event posts (stickied, locked, renamed…) carry a different data-type and must
  // not advance the counter — the position setting refers to comment posts only.
  extend('flarum/forum/components/PostStream', 'view', function (rootVnode) {
    if (!ctaEnabled() || app.session.user) return;
    const pos = getCtaPosition();
    if (pos === 1) return;
    const children = rootVnode == null ? void 0 : rootVnode.children;
    if (!Array.isArray(children)) return;
    let count = 0;
    for (let i = 0; i < children.length; i++) {
      var _children$i;
      const attrs = (_children$i = children[i]) == null ? void 0 : _children$i.attrs;
      if (attrs != null && attrs['data-number'] && (attrs == null ? void 0 : attrs['data-type']) === 'comment') {
        if (++count === pos) {
          children.splice(i + 1, 0, renderCta());
          break;
        }
      }
    }
  });
});